import dayjs from "dayjs";

/**
 * Calculates the totals for an invoice based on the provided items
 * @param {Array} items - Array of invoice items
 * @param {Object} form - Ant Design form instance
 * @returns {Object} - Calculated values for the invoice
 */
export const calculateTotals = (items, form) => {
  // Calculate subtotal with precision, rounded to 3 decimal places
  const calculated_subtotal = Number(
    Number(
      items.reduce((sum, item) => sum + (Number(item.subtotal) || 0), 0)
    ).toFixed(3)
  );

  // Get tax rate from the form
  const taxRate = Number(form.getFieldValue("tax_rate") || 0);

  // Calculate tax amount based on subtotal and tax rate with precision, rounded to 3 decimal places
  const calculated_tax_amount = Number(
    Number((calculated_subtotal * taxRate) / 100).toFixed(3)
  );

  // Get discount amount from the form instead of individual items, rounded to 3 decimal places
  const calculated_discount_amount = Number(
    Number(form.getFieldValue("discount_amount") || 0).toFixed(3)
  );
  const total_amount = Number(form.getFieldValue("total_amount") || 0);

  // Calculate total with precision to avoid floating point errors, rounded to 3 decimal places
  const calculated_total = Number(
    Number(
      calculated_subtotal + calculated_tax_amount - calculated_discount_amount
    ).toFixed(3)
  );

  // Ensure total doesn't exceed 15 digits in total (including decimal places)
  const totalStr = calculated_total.toString();
  const sanitizedTotal =
    totalStr.length > 15
      ? Number(Number(totalStr.substring(0, 15)).toFixed(3))
      : calculated_total;

  return {
    calculated_subtotal,
    calculated_tax_amount,
    calculated_discount_amount,
    calculated_total: sanitizedTotal,
    match: sanitizedTotal === total_amount,
  };
};

/**
 * Calculates installment schedule based on payment terms and issue date
 * @param {Array} installments - Array of installment objects from payment terms
 * @param {Object} issueDate - Issue date as dayjs object
 * @param {Number} total - Total invoice amount
 * @returns {Array} - Calculated installment schedule
 */
export const calculateInstallmentSchedule = (
  installments,
  issueDate,
  total
) => {
  if (!installments || !issueDate) {
    return [];
  }

  const schedule = installments.map((installment) => {
    const dueDate = dayjs(issueDate).add(installment.due_days, "days");
    const amount = Number(total * (installment.percentage / 100));

    return {
      dueDate,
      formattedDueDate: dueDate.format("MMM DD, YYYY"),
      percentage: installment.percentage,
      amount,
      due_days: installment.due_days,
    };
  });

  // Sort by due date
  return schedule.sort((a, b) => a.due_days - b.due_days);
};

/**
 * Handles product change in invoice items
 * @param {Number} value - Selected product ID
 * @param {String|Number} key - Item key
 * @param {Array} products - List of available products
 * @param {Array} invoiceItems - Current invoice items
 * @returns {Array} - Updated invoice items
 */
export const handleProductChange = (value, key, products, invoiceItems) => {
  const product = products.find((p) => p.id === value);
  if (!product) {
    return invoiceItems;
  }

  return invoiceItems.map((item) => {
    if (item.key === key) {
      // Use Number with toFixed for better precision
      const unitPrice = product.price;
      const quantity = Number(item.quantity || 1);
      const taxRate = Number(product.taxRate);

      // Calculate with precision
      const subtotal = Number(unitPrice * quantity);
      const taxAmount = Number((subtotal * taxRate) / 100);
      const discountAmount = Number(item.discountAmount || 0);
      const total = Number(subtotal + taxAmount - discountAmount);

      return {
        ...item,
        productId: product.id,
        description: product.name,
        unitPrice,
        taxRate,
        subtotal,
        taxAmount,
        total,
      };
    }
    return item;
  });
};

/**
 * Handles item field changes in invoice items
 * @param {String|Number} key - Item key
 * @param {String} field - Field name to update
 * @param {*} value - New field value
 * @param {Array} invoiceItems - Current invoice items
 * @returns {Array} - Updated invoice items
 */
export const handleItemChange = (key, field, value, invoiceItems) => {
  return invoiceItems.map((item) => {
    if (item.key === key) {
      const updatedItem = { ...item, [field]: value };

      // Recalculate item totals with precision
      const quantity = Number(updatedItem.quantity || 0);
      const unitPrice = Number(updatedItem.unitPrice || 0);
      const taxRate = Number(updatedItem.taxRate || 0);
      // Ensure discountAmount is a number before calling toFixed
      const discountAmount = Number(updatedItem.discountAmount || 0);

      // Use Number() with toFixed to ensure proper precision
      const subtotal = Number(quantity * unitPrice);
      const taxAmount = Number((subtotal * taxRate) / 100);
      const total = Number(subtotal + taxAmount - discountAmount);

      return {
        ...updatedItem,
        subtotal,
        taxAmount,
        total,
      };
    }
    return item;
  });
};

export const formatInvoiceData = (
  values,
  calculatedValues,
  invoiceItems,
  selectedBrand
) => {
  console.log(values, "form data for invoice");

  // Get currency and check if conversion is needed
  const currency = values.currency || "$";
  const isUSD = currency === "$" || currency === "USD";

  // Import convertToUSD synchronously (will be used if needed)
  // Note: This function should be called with async/await when currency conversion is needed
  // For now, we'll return a flag indicating if conversion is needed
  // The caller (CreateInvoiceModal) will handle the conversion

  return {
    partner: values.partner,
    issue_date: values.issueDate.format("YYYY-MM-DD"),
    // Set default values for required fields
    invoice_type: values.invoiceType || "invoice",
    number: values.number || `INV-${Date.now()}`,
    currency: values.currency, // Keep original currency for display
    status: values.status || "draft",
    // Use calculated values instead of form values to ensure accuracy, rounded to 3 decimal places
    // NOTE: These amounts are in the original currency - will be converted to USD in CreateInvoiceModal
    subtotal: Number(
      Number(calculatedValues.calculated_subtotal || 0).toFixed(3)
    ),
    discount_amount: Number(
      Number(calculatedValues.calculated_discount_amount || 0).toFixed(3)
    ),
    tax_rate: Number((parseFloat(values.tax_rate) || 0).toFixed(3)),
    total_amount: Number(
      Number(calculatedValues.calculated_total || 0).toFixed(3)
    ),
    notes: values.notes || "",
    items: invoiceItems.map((item) => {
      const itemData = {
        // Include product field with the productId from the invoice item
        product: item.productId,
        // Include offer if available - backend will handle null product when offer is present
        offer: item.offerId,
        category: item.categoryId,
        // Account is required
        account: item.accountId,
        // Description is required - use description if provided, otherwise use productName, or fallback to empty string (will be caught by validation)
        description:
          item.description && String(item.description).trim().length > 0
            ? String(item.description).trim()
            : item.productName && String(item.productName).trim().length > 0
            ? String(item.productName).trim()
            : "",
        product_name: item.productName || "",
        product_variation: item.productVariation || "",
        quantity: Number(item.quantity),
        // Round unit_price to 3 decimal places
        // NOTE: This is in original currency - will be converted to USD in CreateInvoiceModal
        unit_price: Number(Number(item.unitPrice || 0).toFixed(3)),
        tax_rate: Number((parseFloat(item.taxRate) || 0).toFixed(3)),
        // Include new fields for product type, print type, and marketing files
        product_type: item.productType || "product",
        print_type: item.printType || null,
        envelope: item.envelope || null, // Marketing file ID
        marketing_offer: item.marketingOffer || null, // Mailing offer ID for print type Marketing
        list: item.listId || null,
        segment: item.segmentId || null,
        // Payment terms per line item (array of payment terms)
        // NOTE: Amounts are in original currency - will be converted to USD in CreateInvoiceModal
        payment_terms: item.payment_terms || [],
      };
      // Include offer: prefer first of offerIds[], fallback to single offerId
      if (Array.isArray(item.offerIds) && item.offerIds.length > 0) {
        itemData.offer = item.offerIds[0];
      } else if (item.offerId) {
        itemData.offer = item.offerId;
      }

      // If an offer is selected, do NOT send product.
      // Let the backend resolve the product from the offer to avoid invalid PK errors.
      if (itemData.offer) {
        delete itemData.product;
      }

      // If product ID is missing, don't include the product field at all
      // This will cause a validation error, but it's better than sending an invalid ID
      if (!itemData.product) {
        delete itemData.product;
      }

      return itemData;
    }),
    // Add brand
    brand: selectedBrand.id,
    // Include existing file URL if we're in edit mode and no new file is uploaded
    file: undefined,
    // Flag to indicate if conversion is needed (for caller to handle)
    _needsCurrencyConversion: !isUSD,
    _originalCurrency: currency,
  };
};

/**
 * Prepares form data for invoice submission
 * @param {Object} invoiceData - Formatted invoice data
 * @param {Object} values - Form values
 * @returns {FormData} - FormData object for API submission
 */
export const prepareFormData = (invoiceData, values) => {
  const formData = new FormData();
  const hasFile = values.file && values.file.length > 0;

  // If we have a file, append it to FormData
  if (hasFile) {
    formData.append("file", values.file[0].originFileObj);
  }

  Object.keys(invoiceData).forEach((key) => {
    if (key.startsWith("_")) return;
    if (key === "items") {
      formData.append(key, JSON.stringify(invoiceData[key]));
    } else if (invoiceData[key] !== undefined) {
      const value = invoiceData[key] === null ? "" : invoiceData[key];
      formData.append(key, value);
    }
  });
  return formData;
};
